/**
 * In-memory cart / working-session buffer.
 * This is not a second ledger. Carts stay in RAM until a real commit
 * (print, bill, or pay) writes Firestore. Restarting the app clears it.
 */

type SessionRecord = {
  sessionId: string;
  storeId: string;
  tableName: string;
  sessionData: any;
  timestamp: number;
  trainingMode?: boolean;
};

type CartRecord = {
  sessionId: string;
  storeId: string;
  timestamp: number;
  items: any[];
};

type OrderRecord = {
  orderId: string;
  storeId?: string;
  sessionId?: string;
  timestamp: number;
  synced?: boolean;
  [key: string]: any;
};

const clone = <T,>(value: T): T => {
  if (value == null) return value;
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value));
  }
};

const wrapCreated = (value: any) => {
  if (!value) return value;
  if (typeof value.toDate === 'function') return value;
  let dateValue: Date;
  if (value instanceof Date) dateValue = value;
  else if (typeof value === 'number' || typeof value === 'string') dateValue = new Date(value);
  else dateValue = new Date(value);
  return { toDate: () => dateValue };
};

const isChannelTableName = (tableName?: string) =>
  !!tableName && (tableName.startsWith('TW') || tableName.startsWith('DL') || tableName.startsWith('CS'));

class CartMemoryStore {
  private cartItems = new Map<string, CartRecord>();
  private sessions = new Map<string, SessionRecord>();
  private customFields = new Map<string, { sessionId: string; storeId: string; fieldValues: any; timestamp: number }>();
  private orders = new Map<string, OrderRecord>();
  private auditSnapshots: any[] = [];
  private auditChanges: any[] = [];
  private auditErrors: any[] = [];
  private legacyDbDeleted = false;

  async init(): Promise<void> {
    this.deleteLegacyIndexedDb();
  }

  private deleteLegacyIndexedDb() {
    if (this.legacyDbDeleted || typeof indexedDB === 'undefined') return;
    this.legacyDbDeleted = true;
    try {
      const request = indexedDB.deleteDatabase('cartDB');
      request.onerror = () => {
        console.warn('[CartMemory] Could not delete leftover cartDB IndexedDB');
      };
    } catch (error) {
      console.warn('[CartMemory] Could not delete leftover cartDB IndexedDB:', error);
    }
  }

  async getDB(): Promise<never> {
    throw new Error('cartDB is an in-memory buffer. Use deleteSessionsByTableName() instead of getDB().');
  }

  async clearAll(): Promise<void> {
    this.cartItems.clear();
    this.sessions.clear();
    this.customFields.clear();
    this.orders.clear();
    this.auditSnapshots = [];
    this.auditChanges = [];
    this.auditErrors = [];
  }

  dump() {
    return {
      cartItems: Array.from(this.cartItems.values()).map(clone),
      sessions: Array.from(this.sessions.values()).map(clone),
      customFields: Array.from(this.customFields.values()).map(clone),
      orders: Array.from(this.orders.values()).map(clone),
      auditSnapshots: clone(this.auditSnapshots),
      auditChanges: clone(this.auditChanges),
      auditErrors: clone(this.auditErrors),
    };
  }

  private getDayCycleStart(stationStartTime: string = '04:00'): Date {
    const now = new Date();
    const dayStart = new Date(now);
    const timeMatch = stationStartTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);
    let hours = 4;
    let minutes = 0;

    if (timeMatch) {
      hours = parseInt(timeMatch[1], 10);
      minutes = parseInt(timeMatch[2], 10);
      const period = timeMatch[3]?.toUpperCase();
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
    }

    dayStart.setHours(hours, minutes, 0, 0);
    if (now < dayStart) {
      dayStart.setDate(dayStart.getDate() - 1);
    }
    return dayStart;
  }

  private async isInTrainingMode(): Promise<boolean> {
    try {
      const storeId = localStorage.getItem('selectedStoreId');
      if (!storeId) return false;
      const posUserData = localStorage.getItem(`currentPOSUser_${storeId}`);
      if (!posUserData) return false;
      const posUser = JSON.parse(posUserData);
      const userId = posUser.id;
      if (!userId) return false;
      const stored = localStorage.getItem(`trainingMode_${userId}`);
      if (!stored) return false;
      const state = JSON.parse(stored);
      if (!state.isTrainingMode || !state.startTime) return false;
      const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
      return elapsed < 5400;
    } catch (error) {
      console.error('[CartMemory] Error checking training mode:', error);
      return false;
    }
  }

  async saveCartItems(sessionId: string, storeId: string, items: any[]): Promise<void> {
    if (!items || items.length === 0) return;
    this.cartItems.set(sessionId, {
      sessionId,
      storeId,
      timestamp: Date.now(),
      items: clone(items),
    });
    if (await this.isInTrainingMode()) return;
    const { noteCartSaved } = await import("../workstationSync");
    noteCartSaved(sessionId, storeId, items);
  }

  async getCartItems(sessionId: string, stationStartTime: string = '04:00'): Promise<any[]> {
    const record = this.cartItems.get(sessionId);
    if (!record?.items?.length) return [];
    const dayCycleStart = this.getDayCycleStart(stationStartTime);
    if (record.timestamp < dayCycleStart.getTime()) return [];
    return clone(record.items);
  }

  async clearCartItems(sessionId: string, seatNumber?: number): Promise<void> {
    const record = this.cartItems.get(sessionId);
    const storeId = record?.storeId || "";
    let remaining: any[] = [];
    if (seatNumber === undefined || seatNumber === null) {
      this.cartItems.delete(sessionId);
    } else {
      if (!record) return;
      remaining = record.items.filter((item) => {
        const itemSeat = item.seatNumber ?? item.itemData?.seatNumber ?? 1;
        return itemSeat !== seatNumber;
      });
      if (remaining.length === 0) this.cartItems.delete(sessionId);
      else this.cartItems.set(sessionId, { ...record, items: remaining, timestamp: Date.now() });
    }
    if (await this.isInTrainingMode()) return;
    const { noteCartSaved } = await import("../workstationSync");
    noteCartSaved(sessionId, storeId, remaining);
  }

  async saveSession(
    sessionId: string,
    storeId: string,
    tableName: string,
    sessionData: any
  ): Promise<void> {
    this.sessions.set(sessionId, {
      sessionId,
      storeId,
      tableName,
      sessionData: clone(sessionData),
      timestamp: Date.now(),
    });
  }

  async getSession(sessionId: string): Promise<any> {
    return clone(this.sessions.get(sessionId)?.sessionData) || null;
  }

  async saveSessionObject(session: any): Promise<void> {
    const normalizedSession = { ...session };
    const isTraining = await this.isInTrainingMode();
    if (isTraining) {
      normalizedSession.trainingMode = true;
    }

    if (normalizedSession.created) {
      let dateValue: Date;
      if (typeof normalizedSession.created.toDate === 'function') {
        dateValue = normalizedSession.created.toDate();
      } else if (normalizedSession.created instanceof Date) {
        dateValue = normalizedSession.created;
      } else {
        dateValue = new Date(normalizedSession.created);
      }
      normalizedSession.created = dateValue.getTime();
    }

    const sessionId = isChannelTableName(session.tableName) ? session.tableName : session.id;
    if (!Object.prototype.hasOwnProperty.call(normalizedSession, 'active')) {
      normalizedSession.active = true;
    }

    this.sessions.set(sessionId, {
      sessionId,
      storeId: session.storeId,
      tableName: session.tableName,
      sessionData: clone(normalizedSession),
      timestamp: Date.now(),
      trainingMode: normalizedSession.trainingMode === true,
    });
    if (normalizedSession.trainingMode === true) return;
    const { noteSessionSaved } = await import("../workstationSync");
    noteSessionSaved({ ...normalizedSession, id: normalizedSession.id || sessionId });
  }

  async getSessionObject(sessionId: string): Promise<any> {
    const session = this.sessions.get(sessionId);
    if (!session?.sessionData) return null;
    const normalized = clone(session.sessionData);
    if (normalized.created) normalized.created = wrapCreated(normalized.created);
    return normalized;
  }

  async getSessionByTableName(storeId: string, tableName: string): Promise<any> {
    const sessionId = isChannelTableName(tableName) ? tableName : null;
    let record = sessionId ? this.sessions.get(sessionId) : null;
    if (!record) {
      record = Array.from(this.sessions.values()).find(
        (s) => s.storeId === storeId && s.sessionData?.tableName === tableName
      ) || null;
    }
    if (!record?.sessionData) return null;
    const normalized = clone(record.sessionData);
    if (normalized.created) normalized.created = wrapCreated(normalized.created);
    return normalized;
  }

  async getAllSessions(storeId: string, includeTraining: boolean = false): Promise<any[]> {
    const mappedSessions = Array.from(this.sessions.values())
      .filter((s) => s.storeId === storeId)
      .map((s) => {
        const sessionData = { ...(s.sessionData || {}) };
        if (sessionData.created) sessionData.created = wrapCreated(sessionData.created);
        return {
          ...sessionData,
          id: s.sessionId,
          sessionId: s.sessionId,
          timestamp: s.timestamp,
          recordTimestamp: s.timestamp,
          trainingMode: s.trainingMode || sessionData.trainingMode || false,
        };
      });

    if (!includeTraining) {
      const isTraining = await this.isInTrainingMode();
      return mappedSessions.filter((s) => (isTraining ? s.trainingMode === true : s.trainingMode !== true));
    }

    return mappedSessions;
  }

  nextParkNumber(storeId: string, prefix: string = 'P'): string {
    const parkPrefix = (prefix || 'P').toUpperCase();
    const pattern = new RegExp(`^${parkPrefix}0*(\\d+)$`, 'i');
    let max = 0;
    for (const record of this.sessions.values()) {
      if (record.storeId && storeId && record.storeId !== storeId) continue;
      const raw = record.sessionData?.parkNumber;
      const match = String(raw || '').match(pattern);
      if (match) max = Math.max(max, Number(match[1]));
    }
    return `${parkPrefix}${String(max + 1).padStart(2, '0')}`;
  }

  async deleteSession(sessionId: string): Promise<void> {
    this.sessions.delete(sessionId);
    this.customFields.delete(sessionId);
  }

  async deleteSessionsByTableName(storeId: string, tableName: string): Promise<string[]> {
    const ids = Array.from(this.sessions.values())
      .filter((record) => (
        (record.storeId === storeId || !record.storeId) &&
        (record.sessionId === tableName || record.tableName === tableName || record.sessionData?.tableName === tableName)
      ))
      .map((record) => record.sessionId);

    for (const sessionId of ids) {
      await this.deleteSession(sessionId);
      await this.clearCartItems(sessionId);
    }
    return ids;
  }

  async updateSessionMetadata(sessionId: string, updates: Record<string, any>): Promise<void> {
    const existing = this.sessions.get(sessionId);
    if (existing) {
      this.sessions.set(sessionId, {
        ...existing,
        storeId: updates.storeId || existing.storeId,
        tableName: updates.tableName || existing.tableName,
        sessionData: { ...existing.sessionData, ...clone(updates) },
        timestamp: Date.now(),
      });
      if (Array.isArray(updates.printHistory)) {
        import("../../components/layout/tableServiceStatus")
          .then((mod) => mod.applyCartStatus(sessionId))
          .catch((error) => console.warn("[cartDB] Table status was not updated", error));
      }
      return;
    }
    this.sessions.set(sessionId, {
      sessionId,
      storeId: updates.storeId || '',
      tableName: updates.tableName || '',
      sessionData: clone(updates),
      timestamp: Date.now(),
    });
  }

  async saveCustomFields(sessionId: string, storeId: string, fieldValues: any): Promise<void> {
    this.customFields.set(sessionId, {
      sessionId,
      storeId,
      fieldValues: clone(fieldValues),
      timestamp: Date.now(),
    });
  }

  async getCustomFields(sessionId: string): Promise<any> {
    return clone(this.customFields.get(sessionId)?.fieldValues) || {};
  }

  async saveOrder(orderId: string, orderData: any): Promise<void> {
    this.orders.set(orderId, {
      ...clone(orderData),
      orderId,
      timestamp: Date.now(),
      synced: orderData?.synced ?? false,
    });
  }

  async getOrder(orderId: string): Promise<any> {
    return clone(this.orders.get(orderId)) || undefined;
  }

  async getUnsyncedOrders(): Promise<any[]> {
    return Array.from(this.orders.values()).filter((order) => order.synced === false).map(clone);
  }

  async markOrderSynced(orderId: string, firestoreId?: string): Promise<void> {
    const order = this.orders.get(orderId);
    if (!order) return;
    this.orders.set(orderId, {
      ...order,
      synced: true,
      firestoreId: firestoreId || order.firestoreId,
      syncedAt: Date.now(),
    });
  }

  async deleteOrder(orderId: string): Promise<void> {
    this.orders.delete(orderId);
  }

  async getOrdersByStore(storeId: string): Promise<any[]> {
    return Array.from(this.orders.values()).filter((order) => order.storeId === storeId).map(clone);
  }

  private mutateSessionData(sessionId: string, updater: (sessionData: any) => any): void {
    const existing = this.sessions.get(sessionId);
    if (!existing) return;
    this.sessions.set(sessionId, {
      ...existing,
      sessionData: updater({ ...(existing.sessionData || {}) }),
      timestamp: Date.now(),
    });
  }

  async activateAuditLock(sessionId: string): Promise<void> {
    this.mutateSessionData(sessionId, (session) => ({
      ...session,
      auditLockActive: true,
      kitchenPrintedAt: Date.now(),
      printCount: (session.printCount || 0) + 1,
    }));
  }

  async isAuditLockActive(sessionId: string): Promise<boolean> {
    const session = await this.getSession(sessionId);
    return session?.auditLockActive === true;
  }

  async addAuditEvent(sessionId: string, event: any): Promise<void> {
    this.mutateSessionData(sessionId, (session) => ({
      ...session,
      auditLog: [...(session.auditLog || []), clone(event)],
    }));
  }

  async addPrintEvent(sessionId: string, printEvent: any): Promise<void> {
    this.mutateSessionData(sessionId, (session) => ({
      ...session,
      printHistory: [...(session.printHistory || []), clone(printEvent)],
      printCount: (session.printCount || 0) + 1,
    }));
  }

  async getAuditLog(sessionId: string): Promise<any[]> {
    const session = await this.getSession(sessionId);
    return session?.auditLog || [];
  }

  async getPrintHistory(sessionId: string): Promise<any[]> {
    const session = await this.getSession(sessionId);
    return session?.printHistory || [];
  }

  async getPrintCount(sessionId: string): Promise<number> {
    const session = await this.getSession(sessionId);
    return session?.printCount || 0;
  }

  async addAuditSnapshot(sessionId: string, snapshot: any): Promise<void> {
    this.auditSnapshots.push({
      ...clone(snapshot),
      sessionId,
      timestamp: snapshot.timestamp || Date.now(),
    });
  }

  async getAuditSnapshots(sessionId: string): Promise<any[]> {
    return clone(this.auditSnapshots.filter((snapshot) => snapshot.sessionId === sessionId));
  }

  async addAuditChangeBatch(sessionId: string, eventId: string, changes: any): Promise<void> {
    this.auditChanges.push({
      changeId: `${eventId}-${Date.now()}`,
      eventId,
      sessionId,
      timestamp: Date.now(),
      changes: clone(changes),
    });
  }

  async getAuditChanges(sessionId: string): Promise<any[]> {
    return clone(this.auditChanges.filter((change) => change.sessionId === sessionId));
  }

  async getAuditHistory(sessionId: string): Promise<{
    snapshots: any[];
    changes: any[];
    events: any[];
    errors: any[];
  }> {
    const [snapshots, changes, events, errors] = await Promise.all([
      this.getAuditSnapshots(sessionId),
      this.getAuditChanges(sessionId),
      this.getAuditLog(sessionId),
      this.getAuditErrors(sessionId),
    ]);
    return { snapshots, changes, events, errors };
  }

  async addAuditError(sessionId: string, error: any): Promise<void> {
    this.auditErrors.push({
      errorId: `error-${sessionId}-${Date.now()}`,
      sessionId,
      timestamp: Date.now(),
      synced: false,
      ...clone(error),
    });
  }

  async getAuditErrors(sessionId: string): Promise<any[]> {
    return clone(this.auditErrors.filter((error) => error.sessionId === sessionId));
  }

  async markAuditErrorsAsSynced(errorIds: string[]): Promise<void> {
    this.auditErrors = this.auditErrors.map((error) => (
      errorIds.includes(error.errorId) ? { ...error, synced: true } : error
    ));
  }
}

const cartDB = new CartMemoryStore();

export default cartDB;
